import React, { FC, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Field, FormikProps } from 'formik';
import { Typography, Grid } from '@inplayer-org/inplayer-ui';
import styled from 'styled-components';
import noop from 'lodash/noop';

// hooks
import useOnMount from 'hooks/useOnMount';

// actions
import { getIvsChannels, getIvsChannelDetails, IvsChannel } from 'ducks/merchantDucks/items/actions';

// components
import Loader from 'components/Loader';
import CommonIcon from 'components/CommonIcon';
import { ContentContainer, CenterContent } from 'components/SharedStyledElements';
import FormikWrapper from 'components/FormikWrapper';
import TaggedLink from 'components/TaggedLink';

// selectors
import { getItemDetails, getIvsChannelsData } from 'ducks/merchantDucks/items/selectors';

// hooks
import useSharedState from 'pages/MerchantPages/Assets/containers/EditAsset/hooks/useSharedState';
import useSharedBoolean from 'pages/MerchantPages/Assets/containers/EditAsset/hooks/useSharedBoolean';
import { useUncontrolledSwitch } from 'components/Switcher';

// own components
import ChannelDropdown from './ChannelOptions/fields/ChannelDropdown';

// own types
import { IvsContentObject, EditState } from '../IvsContent';

const { Container, Cell } = Grid;

const TopMarginCell = styled(Cell)`
  margin-top: 1em;
`;

interface FormValues {
  arn: string;
  stream_url: string;
}

const defaultValues: FormValues = {
  arn: '',
  stream_url: '',
};

interface Props {
  content: IvsContentObject;
  closePanel: () => void;
  onSubmit: (channelContent: IvsContentObject) => void;
  isReadOnly: boolean;
}

const ChannelSelect: FC<Props> = ({ content, closePanel, onSubmit, isReadOnly }) => {
  const { stateSwitcher } = useUncontrolledSwitch();

  const initialValues: FormValues = useMemo(
    () => ({
      ...defaultValues,
      ...content,
    }),
    [content]
  );

  const dispatch = useDispatch();

  const { isFetching: isItemDetailsFetching } = useSelector(getItemDetails);

  const {
    status: { isFetching: isIvsChannelsFetching },
    list,
  } = useSelector(getIvsChannelsData);

  const [, setCurrentChannelArn] = useSharedState('currentChannelArn', initialValues.arn);
  const [, setSaveButtonDisabled] = useSharedBoolean('isSaveButtonDisabled', false);

  const [isEditDisabled, setEditDisabled] = useState(!initialValues.arn);

  useOnMount(() => {
    setCurrentChannelArn(initialValues.arn);
    dispatch(getIvsChannels());
  });

  const options = useMemo(
    () =>
      list.map(({ arn, name }) => ({
        displayName: name,
        value: arn,
      })),
    [list]
  );

  const defaultOption = {
    displayName: 'Select channel',
  };

  const handleSubmit = async (values: FormValues) => {
    if (!values.arn || values.stream_url) {
      onSubmit(values);
    } else {
      const data = await Promise.resolve(dispatch(getIvsChannelDetails(values.arn)));

      if (data) {
        const { stream_url } = data as IvsChannel;

        onSubmit({ ...values, stream_url });
      }
    }
  };

  const handleChange = ({ arn }: FormValues) => {
    setCurrentChannelArn(arn);
    setEditDisabled(!arn);
  };

  const handleStatusChange = ({ dirty }: FormikProps<FormValues>) => {
    setSaveButtonDisabled(!dirty);
  };

  if (isItemDetailsFetching || isIvsChannelsFetching) {
    return <Loader position="absolute" />;
  }

  return (
    <FormikWrapper<FormValues>
      initialValues={initialValues}
      onSubmit={handleSubmit}
      closePanel={closePanel}
      onChange={handleChange}
      onStatusChange={handleStatusChange}
    >
      <ContentContainer>
        <CenterContent>
          <Container columns="1fr auto" rowGap="1rem">
            <Cell width={2}>
              <Typography variant="h6">Existing channel title</Typography>
            </Cell>
            <Cell>
              <Field
                tag="dropdown_channel"
                name="arn"
                component={ChannelDropdown}
                options={options}
                defaultOption={defaultOption}
                disabled={isReadOnly}
              />
            </Cell>
            <Cell center middle>
              <CommonIcon
                tag="icon_button_edit_channel"
                name="edit"
                onClick={!(isReadOnly || isEditDisabled) ? stateSwitcher(EditState.EDIT_CHANNEL) : noop}
                isIconDisabled={isReadOnly || isEditDisabled}
              />
            </Cell>
            <TopMarginCell width={2} center middle>
              or
            </TopMarginCell>
            <Cell width={2} center middle>
              <TaggedLink
                tag="link_create_new_channel"
                onClick={stateSwitcher(EditState.NEW_CHANNEL)}
                disabled={isReadOnly}
              >
                Create a new channel
              </TaggedLink>
            </Cell>
          </Container>
        </CenterContent>
      </ContentContainer>
    </FormikWrapper>
  );
};

export default ChannelSelect;
